<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
        <div class="col-sm-6 leftcol">
          <h3>{{ _('General') }}</h3>
          <div class="form-group">
            <label for="name">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="voucherName" v-model="voucher.name" :placeholder="_('Enter voucher name')" />
          </div>
          <div class="form-group">
            <label for="value">{{ _('Value') }}</label>
            <div class="input-group">
              <input type="number" data-android-inputmode="decimal" step="any" class="form-control" id="voucherValue" v-model="voucher.value" :lang="locale" :placeholder="_('Enter voucher value')" />
              <div class="input-group-btn">
                <Dropdown buttonClass="btn-default" menuClass="dropdown-menu-right">
                  <template #trigger>
                    <span id="voucherTarget" v-if="!parseInt(voucher.unit)">{{ _('Percent') }}</span>
                    <span id="voucherTarget" v-else>{{ settings.currency }}</span> <span class="caret"></span>
                  </template>
                  <li><a id="setVoucherPercent" v-on:click="voucher.unit = '0'">{{ _('Percent') }}</a></li>
                  <li><a id="setVoucherEuros" v-on:click="voucher.unit = '1'">{{ settings.currency }}</a></li>
                </Dropdown>
              </div>
            </div>
            <div class="checkbox">
              <label>
              <input type="checkbox" v-model="voucher.open" id="open" :disabled="!parseInt(voucher.unit)" true-value="1" false-value="0" />
              {{ _('No preset value') }}
              </label>
            </div>
          </div>
          <div class="form-group" id="usableContainer" v-if="parseInt(voucher.unit)">
            <label for="usable">{{ _('Value cards created from this voucher') }}</label>
            <select class="form-control" id="usable" v-model="usable">
              <option value="multi">{{ _('Can be applied to multiple orders') }}</option>
              <option value="singleorder">{{ _('Can be applied to a single order') }}</option>
              <option value="singleuse">{{ _('Can be applied to a single basket item') }}</option>
            </select>
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="voucher.separatevaluecards" true-value="1" false-value="0" :disabled="usable === 'multi'" />
                {{ _('Are created separately for each use') }}
              </label>
            </div>
          </div>
          <div class="form-group">
            <label for="codes">{{ _('Codes') }}</label>
            <textarea class="form-control" wrap="virtual" rows="3" v-model="voucher.codes"></textarea>
          </div>
        </div>
        <div class="col-sm-6 rightcol">
          <div class="form-group">
            <h3>{{ _('Availability') }}</h3>
            <div class="checkbox">
              <label>
              <input type="checkbox" v-model="voucher.shared" id="shared" true-value="1" false-value="0" />
              {{ _('Make this voucher available to merchants using your sales channel') }}
              </label>
            </div>
            <div class="well">
              {{ _('With this setting you can make value cards created from this voucher usable by your submerchants.') }}
              {{ _('Note that any product group restrictions or billing customers will not be enforceable.') }}
            </div>
          </div>
          <div class="form-group" v-if="isNetvisorConfigured">
            <h3>{{ _('Netvisor') }}</h3>
            <div class="form-group">
              <label for="pay_option2_netvisoraccount">{{ _('Netvisor account') }}</label>
              <div class="input-group">
                <input type="text" class="form-control netvisor-account" disabled="disabled" :value="voucher.netvisoraccount" :placeholder="_('default')" />
                <span class="input-group-btn">
                  <button type="button" class="btn btn-info" v-on:click="netvisorAccountDialog(voucher, 'netvisoraccount')">{{ _('Select') }}</button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12"><hr/></div>
        <div class="col-sm-6 leftcol">
          <h3>{{ _('Limit to categories') }}</h3>
          <p>{{ _('Select the categories you want this voucher to be available for and append them here.') }}</p>
          <draggable-multi-selector v-if="voucher && selectedCategories" v-model="selectedCategories" :items="allCategories" itemProperty="name" :selectorTitle="_('Add category')" :noItemsText="_('No categories selected')" />
        </div>
        <div class="col-sm-6 rightcol">
          <h3>{{ _('Billing') }}</h3>
          <p>{{ _('Create a confirmed order for the voucher\'s value to this billing customer.') }}</p>
          <select class="form-control" id="billingCustomerSelector" :placeholder="_('Find billing customer')" v-model="voucher.billingcustomer_id">
            <option value="">{{ _('Not selected') }}</option>
            <option v-for="bc in allBillingcustomers" :value="bc.id">{{ bc.company }}<template v-if="bc.name"> / {{ bc.name }}</template></option>
          </select>
        </div>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-on:click="remove" type="button" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="flex-row">
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import netvisor from '~/lib/netvisor'
import settingshelpers from '~/lib/settingshelpers'
import { DraggableMultiSelector } from '#components'

export default {
  data () {
    return {
      voucher: {},
      allCategories: [],
      allBillingcustomers: [],
      selectedCategories: null
    }
  },
  mixins: [components, netvisor, settingshelpers],
  computed: {
    usable: {
      get () {
        if (this.voucher) {
          if (parseInt(this.voucher.singleuse)) {
            return 'singleuse'
          }
          if (parseInt(this.voucher.singleorder)) {
            return 'singleorder'
          }
        }
        return 'multi'
      },
      set (newValue) {
        this.voucher.singleuse = 0
        this.voucher.singleorder = 0
        switch (newValue) {
          case 'singleuse':
            this.voucher.singleuse = 1
            break
          case 'singleorder':
            this.voucher.singleorder = 1
            break
        }
      }
    }
  },
  methods: {
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'value',
        'unit',
        'singleuse',
        'singleorder',
        'open',
        'separatevaluecards',
        'codes',
        'shared',
        'billingcustomer_id',
        'netvisoraccount'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.voucher[e], a), {})
      data.related = this.selectedCategories.map(c => c.id).join(',')
      return data
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/vouchers/' + this.voucher.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deletion failed!'))
      }
      this.close()
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/vouchers/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/vouchers.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadVoucher(response.data.id)
      }
      this.saving = false
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    loadVoucher (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/vouchers/' + id + '.json?apikey=' + this.apiKey)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        this.voucher = response.data
        if (this.voucher.codes) {
          this.voucher.codes = this.voucher.codes.split(';').filter(c => c).join(';')
        }
        await this.loadVoucherCategories()
        this.storeState()
        this.loading = false
        resolve(this.voucher)
      })
    },
    async loadCategories () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/categories.json?iSortingCols=1&iSortCol_0=1&sSortDir=asc&apikey=' + this.apiKey)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allCategories = response.data
    },
    loadVoucherCategories () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/vouchers/' + this.voucher.id + '/categories.json?iSortingCols=1&iSortCol_0=1&sSortDir=asc&apikey=' + this.apiKey)
        } catch(e) {
          this.showError(e)
          reject(e)
          return
        }
        this.selectedCategories = response.data
        resolve(response.data)
      })
    },
    async loadBillingcustomers () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/billingcustomers.json?iSortingCols=1&iSortCol_0=1&sSortDir=asc&apikey=' + this.apiKey)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allBillingcustomers = response.data
    }
  },
  watch: {
    usable (newValue) {
      if (newValue === 'multi') {
        this.voucher.separatevaluecards = 1
      }
    }
  },
  components: {
    DraggableMultiSelector
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadVoucher(this.component.initData.voucherId)
    }
  },
  mounted () {
    this.loadCategories()
    this.loadBillingcustomers()
    if(!this.events) {
      let events = {}
      events['voucherUpdate'] = async (message) => {
        if (message && message.data && this.voucher && parseInt(message.data.id, 10) === parseInt(this.voucher.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadVoucher(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.voucher && this.voucher.id && parseInt(this.voucher.id, 10) !== parseInt(this.component.initData.voucherId, 10)) {
        try {
          await this.loadVoucher(this.component.initData.voucherId)
        } catch(e) {}
      }
    }
  }
}
</script>